import React, { useEffect, useRef, useState } from 'react';
import { deleteGalleryItem, fetchGallery, uploadGalleryImages } from '../../api/galleryApi';

const AdminGallery = () => {
  const fileInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(24);
  const [totalPages, setTotalPages] = useState(1);

  const load = async (nextPage = page) => {
    setLoading(true); setErr('');
    try {
      const data = await fetchGallery({ page: nextPage, limit });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotalPages(Number(data?.totalPages || 1));
      setPage(Number(data?.page || nextPage));
    } catch (e) { setErr(e?.message || 'Failed to load gallery');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(1); }, [limit]);

  const onPickFiles = () => fileInputRef.current?.click();

  const onFilesChange = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append('images[]', f));
    setUploading(true); setErr('');
    try {
      await uploadGalleryImages(fd);
      await load(1);
      setPage(1);
    } catch (ex) { setErr(ex?.message || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const onDelete = async (g) => {
    if (!window.confirm('Delete this image?')) return;
    try { await deleteGalleryItem(g.id); await load(page); } catch (ex) { window.alert(ex?.message || 'Delete failed'); }
  };

  return (
    <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: '#F9FAFB' }}>Gallery</h1>
          <p style={{ margin: '6px 0 0', color: '#9CA3AF', fontSize: '14px' }}>Upload and manage gallery images</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onPickFiles} disabled={uploading}
            style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #FFD700, #FFA500)', color: '#0B1020', fontWeight: 600, fontSize: '14px', cursor: uploading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', opacity: uploading ? 0.6 : 1 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            {uploading ? 'Uploading...' : 'Upload Images'}
          </button>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" multiple style={{ display: 'none' }} onChange={onFilesChange} />
        </div>
      </div>

      {err && (
        <div style={{ padding: '14px 16px', background: 'rgba(239,68,68,0.1)', borderRadius: '10px', border: '1px solid rgba(239,68,68,0.2)', color: '#FCA5A5', fontSize: '14px', marginBottom: '20px' }}>{err}</div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>Loading...</div>
      ) : (
        <>
          {items.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280', background: '#0F1B2D', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
              No images yet. Click <strong style={{ color: '#FFD700' }}>Upload Images</strong> to get started.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
              {items.map((g) => (
                <div key={g.id} style={{ background: '#0F1B2D', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden', transition: 'all 0.25s ease', position: 'relative', aspectRatio: '1' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,215,0,0.3)'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.3)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                  <img src={g.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  <div style={{ position: 'absolute', bottom: '8px', right: '8px', display: 'flex', gap: '6px', opacity: 0, transition: 'opacity 0.2s' }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0'; }}>
                    <button onClick={() => onDelete(g)} style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: 'rgba(239,68,68,0.9)', color: '#FFF', cursor: 'pointer', fontWeight: 500, fontSize: '12px', backdropFilter: 'blur(4px)' }}>Delete</button>
                  </div>
                  <div className="gallery-overlay" style={{ position: 'absolute', bottom: '8px', right: '8px' }}>
                    <button onClick={() => onDelete(g)} style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: 'rgba(239,68,68,0.85)', color: '#FFF', cursor: 'pointer', fontWeight: 500, fontSize: '12px', backdropFilter: 'blur(4px)', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,1)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.85)'; }}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={() => load(page - 1)} disabled={page <= 1}
                style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid rgba(255,215,0,0.2)', background: 'transparent', color: page <= 1 ? '#4B5563' : '#FFD700', cursor: page <= 1 ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 500 }}>
                Previous
              </button>
              <span style={{ color: '#6B7280', fontSize: '13px' }}>Page {page} of {totalPages}</span>
              <button onClick={() => load(page + 1)} disabled={page >= totalPages}
                style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid rgba(255,215,0,0.2)', background: 'transparent', color: page >= totalPages ? '#4B5563' : '#FFD700', cursor: page >= totalPages ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 500 }}>
                Next
              </button>
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default AdminGallery;
